import {
    Alert,
    Button,
    ColorIndicator,
    ConfirmModal,
    DataTable,
    Icon,
    IconButton,
    Loading,
    Spacer,
    StyleFunction,
    Surface,
    useAuth,
    useModal,
    usePagination,
    useSearch,
    useTheme,
    useThemedStyle,
} from "@venuepos/react-common";

import { RouteProp, useNavigation, useRoute } from "@react-navigation/native";
import {
    GQApprovalTokenGroupsQuery,
    useApprovalTokenGroupDeleteMutation,
    useApprovalTokenGroupsQuery,
} from "graphql-sdk";
import { formatDateTime, SearchDefinition } from "lib";
import { AvailableLocale, useTranslation } from "locales";
import React, { useCallback, useEffect } from "react";
import { View } from "react-native";
import { useHandleMutationError, useSearchDefinition } from "../../../hooks";
import { RootStackParamList } from "../../../navigation";
import { useAdminSession } from "../../../session";
import { AdminContainer } from "../../container";

export function ApprovalTokenGroupTab() {
    const auth = useAuth();
    auth.enforce(["merchant.approval"]);

    const styles = useThemedStyle(styleFunc);
    const [{ locale }] = useAdminSession(["locale"]);
    const theme = useTheme();
    const [t] = useTranslation();
    const { render } = useModal();
    const { navigate } = useNavigation();
    const route =
        useRoute<RouteProp<RootStackParamList, "APPROVAL_TOKEN_GROUPS">>();

    const [approvalTokenGroupDelete] = useApprovalTokenGroupDeleteMutation();
    const { handleMutationError } = useHandleMutationError();

    const { createSearchDefinition } = useSearchDefinition();
    const defaultApprovalTokenGroupSearch: SearchDefinition = {
        name: createSearchDefinition("name"),
        createdAt: createSearchDefinition("createdAt"),
    };

    const {
        component: searchComponent,
        indicator: searchIndicator,
        search,
    } = useSearch(defaultApprovalTokenGroupSearch, {
        visible: true,
        onSubmit: () => {
            // When the search form is submitted, then return the pagination to the first page.
            onPageChange(0);
        },
    });

    const {
        page,
        pageSize,
        sortBy,
        sortDirection,
        onSortChange,
        onPageChange,
        onPageSizeChange,
    } = usePagination({
        initialSortBy: "name",
        initialSortDirection: "ASC",
    });

    const { data, loading, error, refetch } = useApprovalTokenGroupsQuery({
        variables: {
            pagination: {
                page,
                pageSize,
                sort: sortBy,
                sortDirection: sortDirection,
            },
            searching: search,
        },
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        if (route.params?.refetch) {
            refetch();
        }
    }, [refetch, route.params?.refetch]);

    const handleEdit = useCallback(
        (id: string) => {
            navigate("APPROVAL_TOKEN_GROUP_EDIT", {
                id,
            });
        },
        [navigate]
    );

    const handleDelete = useCallback(
        async (id: string) => {
            if (
                !(await render(onClose => (
                    <ConfirmModal
                        headerText={t(
                            "backoffice.approval_token_group.delete",
                            "Delete approval token group?"
                        )}
                        bodyText={t(
                            "backoffice.approval_token_group.delete_explain",
                            "Are you sure, you want to delete the approval token group?"
                        )}
                        onClose={onClose}
                    />
                )))
            ) {
                return;
            }

            await handleMutationError(
                async () =>
                    await approvalTokenGroupDelete({
                        variables: {
                            id: id,
                        },
                    }),
                t(
                    "backoffice.approval_token_group.deleted",
                    "Approval token group deleted"
                ),
                async () => {
                    await refetch();
                }
            );
        },
        [approvalTokenGroupDelete, handleMutationError, refetch, render, t]
    );

    const renderCmpEventRow = useCallback(
        (
            item: GQApprovalTokenGroupsQuery["approvalTokenGroups"]["data"][0],
            rowIndex
        ) => (
            <DataTable.Row
                style={rowIndex % 2 ? theme.styles.oddRow : undefined}
                onPress={() => handleEdit(item.id)}
                key={`approvalId_${item.id}`}
            >
                <DataTable.Cell style={styles.iconColumn}>
                    <ColorIndicator
                        status={item.active ? "success" : "warning"}
                    />
                </DataTable.Cell>
                <DataTable.Cell>{item.name}</DataTable.Cell>
                <DataTable.Cell>
                    {formatDateTime(item.createdAt, locale as AvailableLocale)}
                </DataTable.Cell>
                <DataTable.Cell>
                    {formatDateTime(item.updatedAt, locale as AvailableLocale)}
                </DataTable.Cell>
                <DataTable.Cell style={styles.iconColumn} numeric>
                    <IconButton
                        color={theme.colors.secondary}
                        name="edit"
                        onPress={() => handleEdit(item.id)}
                        testID={"edit:" + item.name}
                    />
                    <IconButton
                        color={theme.colors.secondary}
                        name="delete"
                        onPress={() => handleDelete(item.id)}
                        testID={"delete:" + item.name}
                    />
                </DataTable.Cell>
            </DataTable.Row>
        ),
        [
            theme.styles.oddRow,
            theme.colors.secondary,
            styles.iconColumn,
            locale,
            handleEdit,
            handleDelete,
        ]
    );

    if (error) {
        return (
            <Alert type="error">
                {t(
                    "backoffice.error.from_server",
                    "There was an error: {{errorText}}",
                    {
                        errorText: error?.message,
                    }
                )}
            </Alert>
        );
    }

    return (
        <AdminContainer>
            <View style={[theme.styles.row, styles.container]}>
                <Button
                    onPress={() => {
                        navigate("APPROVAL_TOKEN_GROUP_CREATE");
                    }}
                    style={styles.button}
                >
                    {t("backoffice.common.create", "Create")}
                </Button>
                <Spacer space={1} />
                {searchIndicator}
            </View>
            <Spacer space={2} />
            <Surface>
                {searchComponent}
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "active" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("active")}
                            style={styles.iconColumn}
                        >
                            {t("backoffice.common.active", "Active")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "name" ? sortDirection : undefined
                            }
                            onPress={() => onSortChange("name")}
                        >
                            {t("backoffice.approval_token_group.name", "Name")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "createdAt"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("createdAt")}
                        >
                            {t("common.created_at", "Created at")}
                        </DataTable.Title>
                        <DataTable.Title
                            sortDirection={
                                sortBy === "updatedAt"
                                    ? sortDirection
                                    : undefined
                            }
                            onPress={() => onSortChange("updatedAt")}
                        >
                            {t("common.updated_at", "Updated at")}
                        </DataTable.Title>
                        <DataTable.Title style={styles.iconColumn} numeric>
                            <Icon name="sort" color={theme.colors.black} />
                        </DataTable.Title>
                    </DataTable.Header>
                    {loading ? (
                        <Loading />
                    ) : data.approvalTokenGroups.data.length === 0 ? (
                        <DataTable.Row style={theme.styles.oddRow}>
                            <DataTable.Cell>
                                {t(
                                    "backoffice.approval_token_group.no_data",
                                    "No approval token groups."
                                )}
                            </DataTable.Cell>
                        </DataTable.Row>
                    ) : (
                        data?.approvalTokenGroups.data.map(renderCmpEventRow)
                    )}
                    <DataTable.Pagination
                        onPageChange={onPageChange}
                        pageSize={pageSize}
                        onSizeChange={onPageSizeChange}
                        page={page}
                        numberOfPages={
                            data?.approvalTokenGroups.pagination.pages
                        }
                        itemCount={
                            data?.approvalTokenGroups.pagination.resultCount
                        }
                    />
                </DataTable>
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = () => ({
    container: {
        justifyContent: "flex-end",
        flexWrap: "wrap",
    },
    iconColumn: { flexGrow: 0, flexShrink: 0, flexBasis: 60 },
});
